import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { publicApiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import logo from "@/assets/connxt.png";
import logo_desktop from "@/assets/fynxt_blue.png";
import PostLoginLandingPage from "@/pages/common/PostLoginLandingPage";
import { useAuthStore } from "@/store/auth.store";

export interface Brand {
	id: string;
	name: string;
}

export interface UserSession {
	scope: string;
	brands: Brand[];
}
const LoginPage: React.FC = () => {
	const {
		setAuth,
		setBrandDetails,
		setScope,
		setIsForgotPasswordLoading,
		setIsLoggedIn
	} = useAuthStore();
	const { isBrand, brandId, isSuperAdmin, isLoggedIn, scope, brandDetails } =
		useAuthStore();

	const [isLoading, setIsLoading] = useState(false);
	const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
	const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
	const [isForgotPasswordLoading, setLocalForgotPasswordLoading] =
		useState(false);
	const [errors, setErrors] = useState({
		email: "",
		password: "",
		general: ""
	});
	const [forgotPasswordResponse, setForgotPasswordResponse] = useState({
		email: "",
		general: ""
	});
	const [forgotPasswordErrors, setForgotPasswordErrors] = useState({
		email: "",
		general: ""
	});
	const [formData, setFormData] = useState({
		email: "",
		password: ""
	});

	// Redirect if already logged in
	if (isBrand && !isSuperAdmin) {
		return <Navigate to={`/${brandId}`} replace />;
	}

	const validateForm = () => {
		const newErrors = {
			email: "",
			password: "",
			general: ""
		};

		if (!formData.email) {
			newErrors.email = "Email is required";
		} else if (
			!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
		) {
			newErrors.email = "Invalid email address";
		}

		if (!formData.password) {
			newErrors.password = "Password is required";
		} else if (formData.password.length < 8) {
			newErrors.password = "Password must be at least 8 characters";
		}

		setErrors(newErrors);

		return !Object.values(newErrors).some((error) => error !== "");
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;

		setFormData((prev) => ({ ...prev, [name]: value }));

		setErrors((prev) => ({ ...prev, [name]: "", general: "" }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsLoading(true);

		setErrors((prev) => ({ ...prev, general: "" }));

		try {
			const loginRes = await publicApiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["AUTH", "LOGIN"]),
				{
					email: formData.email,
					password: formData.password
				}
			);

			const { refreshToken, brands, scope } = loginRes.data;

			if (!refreshToken) {
				setErrors((prev) => ({
					...prev,
					general: "Invalid login credentials"
				}));

				return;
			}

			setAuth({ accessToken: "", refreshToken, expiresAt: "" });
			setBrandDetails(JSON.stringify(brands));
			setScope(scope);
			setIsLoggedIn(true);
		} catch (_err) {
			setErrors((prev) => ({
				...prev,
				general: "Login failed. Please check your credentials and try again."
			}));
		} finally {
			setIsLoading(false);
		}
	};

	const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!forgotPasswordEmail) {
			setForgotPasswordErrors((prev) => ({
				...prev,
				general: "Email is required"
			}));
			return;
		}

		if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(forgotPasswordEmail)) {
			setForgotPasswordErrors((prev) => ({
				...prev,
				general: "Please enter a valid email address"
			}));
			return;
		}

		try {
			setLocalForgotPasswordLoading(true);
			setIsForgotPasswordLoading(true);
			await publicApiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["USER", "FORGOT_PASSWORD"]),
				{ email: forgotPasswordEmail }
			);
			setForgotPasswordResponse({
				email: forgotPasswordEmail,
				general: "Password reset instructions sent to your email"
			});
			setForgotPasswordEmail("");
		} catch (_error) {
			setForgotPasswordErrors({
				email: forgotPasswordEmail,
				general: "Failed to send reset instructions. Please try again."
			});
		} finally {
			setLocalForgotPasswordLoading(false);
			setIsForgotPasswordLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex flex-col md:flex-row max-w-full w-full">
			<div className="w-full md:w-1/1 bg-[#002666]">
				<div className="flex items-center justify-center h-full">
					<img src={logo} alt="Logo" className="object-cover max-w-full" />
				</div>
			</div>

			<div className="w-full md:w-1/3 bg-white">
				<div className="flex min-h-full flex-col px-6 lg:px-8">
					{isLoggedIn && scope && brandDetails ? (
						<PostLoginLandingPage
							scope={scope}
							brands={JSON.parse(brandDetails)}
						/>
					) : (
						<div>
							<div className="sm:mx-auto sm:w-full sm:max-w-sm mt-24">
								<img
									src={logo_desktop}
									alt="Logo"
									className="object-cover h-full max-w-full mx-auto text-center"
								/>
								<h2 className="font-semibold leading-9 mt-2 text-center text-gray-500 tracking-tight">
									{isForgotPasswordOpen
										? "Reset your password"
										: "Sign in to your account"}
								</h2>
							</div>

							<div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
								{isForgotPasswordOpen ? (
									<form
										className="space-y-6"
										onSubmit={handleForgotPassword}
										noValidate
									>
										{forgotPasswordResponse.general ? (
											<div className="flex flex-col gap-3">
												<div className="rounded-md bg-green-50 p-4">
													<p className="text-sm text-green-700" role="alert">
														{forgotPasswordResponse.general}
													</p>
												</div>
												<button
													type="button"
													onClick={() => {
														setIsForgotPasswordOpen(false);
														setForgotPasswordErrors({
															email: "",
															general: ""
														});
														setForgotPasswordEmail("");
														setForgotPasswordResponse({
															email: "",
															general: ""
														});
													}}
													className="w-full text-center text-sm text-indigo-600 hover:text-indigo-500 rounded-md"
												>
													Back to Sign In
												</button>
											</div>
										) : (
											<>
												<div className="mb-4">
													<label
														htmlFor="forgotPasswordEmail"
														className="block text-sm font-medium text-gray-700 mb-1 text-left"
													>
														Email Address
													</label>
													<input
														type="email"
														value={forgotPasswordEmail}
														onChange={(e) => {
															setForgotPasswordEmail(e.target.value);
															setForgotPasswordErrors((prev) => ({
																...prev,
																general: ""
															}));
														}}
														className={`block w-full rounded-md border-0 py-3 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
															forgotPasswordErrors.general
																? "ring-red-300"
																: "ring-gray-300"
														} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
														placeholder="Enter your email"
														disabled={isForgotPasswordLoading}
													/>
												</div>
												<div className="flex flex-col gap-3">
													{forgotPasswordErrors.general && (
														<div className="rounded-md bg-red-50 p-4">
															<p className="text-sm text-red-700" role="alert">
																{forgotPasswordErrors.general}
															</p>
														</div>
													)}
													<button
														type="submit"
														className="flex w-full justify-center rounded-md [letter-spacing:inherit] bg-[#002666] px-3 py-2 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
														disabled={isForgotPasswordLoading}
													>
														{isForgotPasswordLoading ? (
															<div className="flex items-center">
																<FontAwesomeIcon
																	icon={faSpinner}
																	spin
																	className="mr-2"
																/>
																<span>Sending...</span>
															</div>
														) : (
															"Reset Password"
														)}
													</button>
													<div className="relative">
														<div className="absolute inset-0 flex items-center">
															<div className="w-full border-t border-gray-300" />
														</div>
														<div className="relative flex justify-center text-sm">
															<span className="bg-white px-2 text-gray-500">
																or
															</span>
														</div>
													</div>
													<button
														type="button"
														onClick={() => {
															setIsForgotPasswordOpen(false);
															setForgotPasswordErrors({
																email: "",
																general: ""
															});
															setForgotPasswordEmail("");
															setForgotPasswordResponse({
																email: "",
																general: ""
															});
														}}
														className="w-full text-center text-sm text-indigo-600 hover:text-indigo-500 rounded-md"
													>
														Back to Sign In
													</button>
												</div>
											</>
										)}
									</form>
								) : (
									<form
										className="space-y-6"
										onSubmit={handleSubmit}
										noValidate
									>
										<div>
											<label
												htmlFor="username"
												className="block text-sm font-medium text-gray-700 text-left mb-1"
											>
												Email Address
											</label>
											<input
												name="email"
												type="email"
												required
												aria-invalid={!!errors.email}
												aria-describedby={
													errors.email ? "email-error" : undefined
												}
												value={formData.email}
												onChange={handleChange}
												className={`block w-full rounded-md border-0 py-3 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
													errors.email ? "ring-red-500" : "ring-gray-300"
												} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
												placeholder="Enter your email"
												disabled={isLoading}
											/>
											{errors.email && (
												<p
													className="mt-1 text-sm text-red-600 text-left"
													role="alert"
												>
													{errors.email}
												</p>
											)}
										</div>

										<div>
											<label
												htmlFor="password"
												className="block text-sm font-medium text-gray-700 text-left mb-1"
											>
												Password
											</label>
											<input
												name="password"
												type="password"
												required
												aria-invalid={!!errors.password}
												aria-describedby={
													errors.password ? "password-error" : undefined
												}
												value={formData.password}
												onChange={handleChange}
												className={`block w-full rounded-md border-0 py-3 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
													errors.password ? "ring-red-500" : "ring-gray-300"
												} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
												placeholder="Enter your password"
												disabled={isLoading}
											/>
											{errors.password && (
												<p
													className="mt-1 text-sm text-red-600 text-left"
													role="alert"
												>
													{errors.password}
												</p>
											)}
										</div>

										{errors.general && (
											<div className="rounded-md bg-red-50 p-4">
												<p className="text-sm text-red-700" role="alert">
													{errors.general}
												</p>
											</div>
										)}

										<div className="flex flex-col gap-3">
											<button
												type="submit"
												className="flex w-full justify-center rounded-md [letter-spacing:inherit] bg-[#002666] px-3 py-2 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
												disabled={isLoading}
												aria-busy={isLoading}
											>
												{isLoading ? (
													<div className="flex items-center">
														<FontAwesomeIcon
															icon={faSpinner}
															spin
															className="mr-2"
														/>
														<span>Signing in...</span>
													</div>
												) : (
													"Sign in"
												)}
											</button>
											<div className="relative">
												<div className="absolute inset-0 flex items-center">
													<div className="w-full border-t border-gray-300" />
												</div>
												<div className="relative flex justify-center text-sm">
													<span className="bg-white px-2 text-gray-500">
														or
													</span>
												</div>
											</div>
											<button
												type="button"
												onClick={() => {
													setIsForgotPasswordOpen(true);
													setErrors({
														email: "",
														password: "",
														general: ""
													});
													setFormData({
														email: "",
														password: ""
													});
												}}
												className="text-center text-sm font-medium text-indigo-600 hover:text-indigo-500 rounded-md"
											>
												Forgot your password?
											</button>
										</div>
									</form>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default LoginPage;
